<template>
    <div
        class="scrim"
        @click.prevent.stop="close"
    ></div>

    <div
        v-qa="dataQa"
        class="modal-with-header-and-footer"
        :class="classModalWithHeaderAndFooter"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleElementId"
    >
        <div class="header">
            <slot name="indicator"></slot>

            <h3 class="title">
                <slot name="title"></slot>
            </h3>

            <div class="close">
                <ButtonRound
                    v-qa="'modal-close'"
                    :icon="'close-bold'"
                    :type="'link'"
                    color="var(--stone-2)"
                    aria-label="Close modal"
                    @click="close"
                />
            </div>
        </div>

        <div class="content">
            <slot name="default"></slot>
        </div>

        <div
            v-if="$slots['footer']"
            class="footer"
        >
            <slot name="footer"></slot>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'

const { isFilterModal, dataQa = undefined } = defineProps<{
    dataQa?: string
    isFilterModal?: boolean
    titleElementId: string
}>()

const emit = defineEmits<{
    close: []
}>()

const close = () => {
    emit('close')
}

const classModalWithHeaderAndFooter = computed(() => ({
    'filter-modal': isFilterModal,
}))
</script>

<style lang="scss" scoped>
.modal-with-header-and-footer {
    --header-height: 50px;
    --footer-height: 70px;
    --max-height: 100vh;
    --max-width: 100vw;

    --background: #{color.channel($white, 'red', $space: rgb)
        color.channel($white, 'green', $space: rgb)
        color.channel($white, 'blue', $space: rgb)};
    --background-blurred: rgb(var(--background) / 45%);

    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: auto;
    width: 100vw;
    height: 100vh;
    max-width: var(--max-width);
    max-height: var(--max-height);
    align-self: center;
    background: rgb(var(--background));
    flex-direction: column;
    display: flex;
    overflow: hidden;

    @include min-width(1024) {
        --max-height: 80vh;
        --max-width: 1040px;
        height: auto;
        top: 0;
        bottom: 0;
        min-height: initial;
        margin: auto;
    }

    &.filter-modal {
        @include min-width(1024) {
            --max-height: 100vh;
            --max-width: 570px;

            inset: 0;
            top: 0;
            bottom: 0;
            height: 100vh;

            left: unset;
        }

        .content {
            padding-top: calc(var(--header-height) / 4);
            height: 100vh;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
        }

        .footer {
            position: fixed;
        }
    }

    .header,
    .footer {
        background: var(--background-blurred);
        backdrop-filter: blur(15px);
    }

    .header {
        width: 100%;
        min-height: var(--header-height);
        @include border-bottom;
        position: sticky;
        top: 0;
        left: 0;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 3;

        .title {
            font-size: 0.875rem;
            text-transform: uppercase;
            letter-spacing: 0.05rem;
            color: $slate;
            font-family: $effra-font-stack;
            font-weight: 400;
        }

        .close {
            position: absolute;
            top: 0;
            right: 0;
        }
    }

    .content {
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        align-items: center;
        padding-bottom: calc(var(--footer-height) * 2);
        height: calc(100vh - var(--header-height) - var(--footer-height));

        @include min-width(1024) {
            height: auto;
            padding-top: calc(var(--header-height) / 2);
            padding-bottom: calc(var(--footer-height) / 2);
        }
    }

    .footer {
        width: 100%;
        max-width: var(--max-width);
        min-height: var(--footer-height);
        border-top: 1px solid $grey-200;
        position: fixed;
        bottom: 0;

        flex-shrink: 0;
        display: flex;
        align-items: center;
        z-index: 2;
        padding: 0 16px;

        @include min-width(1024) {
            position: sticky;
            padding: 0 24px;
        }
    }
}

.scrim {
    @include scrim;
}
</style>
